import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faRotate, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import {
  ApiClientError,
  getTeacherCourses,
  postTeacherCourse,
  postUpgradeAllCourses,
  type TeacherCourseRecord,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { CourseCard } from "./CourseCard";

function publishPill(status?: string) {
  if (status === "approved") return <span className="teacher-pill teacher-pill--published">Published</span>;
  if (status === "pending_review") return <span className="teacher-pill teacher-pill--pending">In review</span>;
  return null;
}

export default function TeacherCourseListPage({ embedded = false }: { embedded?: boolean }) {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<TeacherCourseRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [upgradingAll, setUpgradingAll] = useState(false);
  const [upgradeReport, setUpgradeReport] = useState<
    { courseId: string; title: string; toVersion: number }[] | null
  >(null);
  const [upgradeCta, setUpgradeCta] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!bearer) return;
    setError(null);
    setLoading(true);
    try {
      const data = await getTeacherCourses(bearer, { limit: 200, offset: 0 });
      setCourses(data.courses);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load courses");
    } finally {
      setLoading(false);
    }
  }, [bearer]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleCreateManually() {
    if (!bearer || creating) return;
    const title = window.prompt("Course title");
    if (!title?.trim()) return;
    const desc = window.prompt("Description (optional)") ?? "";
    setCreating(true);
    setError(null);
    setUpgradeCta(null);
    try {
      const { course } = await postTeacherCourse(bearer, {
        title: title.trim(),
        description: desc.trim() || undefined,
      });
      navigate(`/teacher/courses/${course.id}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Create failed";
      setError(msg);
      if (err instanceof ApiClientError && (err.code === "BILLING_REQUIRED" || err.code === "LESSON_LIMIT_REACHED")) {
        setUpgradeCta("Upgrade your plan in Billing to continue creating templates and lessons.");
      }
    } finally {
      setCreating(false);
    }
  }

  async function handleUpgradeAll() {
    if (!bearer || upgradingAll) return;
    setUpgradingAll(true);
    setError(null);
    try {
      const { upgraded } = await postUpgradeAllCourses(bearer);
      setUpgradeReport(upgraded);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upgrade failed");
    } finally {
      setUpgradingAll(false);
    }
  }

  const actions = (
    <div className={`teacher-library-actions${embedded ? " teacher-embedded-actions" : ""}`}>
      <Link to="/teacher/courses/new/ai" className="btn primary">
        <FontAwesomeIcon icon={faWandMagicSparkles} /> Create with AI
      </Link>
      <button type="button" className="btn secondary" disabled={creating} onClick={() => void handleCreateManually()}>
        <FontAwesomeIcon icon={faPlus} /> Create manually
      </button>
      <button type="button" className="btn secondary" disabled={upgradingAll} onClick={() => void handleUpgradeAll()}>
        <FontAwesomeIcon icon={faRotate} /> {upgradingAll ? "Upgrading…" : "Upgrade all"}
      </button>
    </div>
  );

  const body = (
    <>
      {error ? (
        <div className="warn-banner" role="alert">
          {error}
          {upgradeCta ? (
            <>
              {" "}
              <Link to="/teacher/billing">Manage billing</Link>
            </>
          ) : null}
        </div>
      ) : null}

      {embedded ? actions : null}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : courses.length === 0 ? (
        <p className="muted">
          No courses yet. Use <strong>Create with AI</strong> to assemble a path from your lessons, or{" "}
          <strong>Create manually</strong> to start empty.
        </p>
      ) : (
        <div className="teacher-course-grid">
          {courses.map((c) => (
            <CourseCard
              key={c.id}
              title={c.title}
              description={c.description}
              heroImageUrl={c.heroImageUrl}
              accentColor={c.accentColor}
              statsLine={`${c.activeCohorts ?? 0} active cohort${(c.activeCohorts ?? 0) === 1 ? "" : "s"} · v${c.version ?? 1}`}
              badges={
                <>
                  {publishPill(c.communityPublishStatus)}
                  {c.sourceCommunityCourseId ? (
                    <span className="teacher-pill teacher-pill--inspired">Inspired</span>
                  ) : null}
                </>
              }
              detailsHref={`/teacher/courses/${c.id}`}
              manageHref={`/teacher/courses/${c.id}/manage`}
              onOpenDetails={() => navigate(`/teacher/courses/${c.id}`)}
            />
          ))}
        </div>
      )}

      {upgradeReport !== null ? (
        <div className="ccs-modal-overlay" role="dialog" aria-modal="true" aria-label="Upgrade results">
          <div className="teacher-modal-card">
            <h2 className="teacher-modal-title">Upgrade complete</h2>
            {upgradeReport.length === 0 ? (
              <p className="muted">All inspired courses were already up to date.</p>
            ) : (
              <ul className="teacher-changelog-list">
                {upgradeReport.map((r) => (
                  <li key={r.courseId} className="small">
                    <strong>{r.title}</strong> upgraded to v{r.toVersion}
                  </li>
                ))}
              </ul>
            )}
            <div className="teacher-modal-actions">
              <span style={{ flex: 1 }} />
              <button type="button" className="btn primary" onClick={() => setUpgradeReport(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );

  if (embedded) return body;

  return (
    <div className="teacher-page submissions-dashboard">
      <header className="submissions-hero overview-hero">
        <div className="submissions-hero-text">
          <p className="eyebrow">Teacher · Courses</p>
          <h1 className="teacher-page-title">My courses</h1>
          <p className="teacher-page-lede">
            Reusable blueprints: the default lesson sequence students follow. Build one with AI, or start empty.
          </p>
        </div>
        {actions}
      </header>
      {body}
    </div>
  );
}
